import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Trash2, Edit, X, Save, CheckSquare, Square, ArrowLeft, ShieldOff, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

const AVAILABLE_MENUS = [
    "Modul Invoice A", "Modul Invoice C", "Modul Cargo", "Modul Storage", "Modul Delivery", "Modul Customs Clearance", "Modul Debit Note", "Modul DDP VN", "Modul History", "Modul Faktur Pajak", "Modul NTPN", "Modul Import Customer", "Modul Storage MYOB", "Modul Overseas Billing", "Modul Local Courier", "Modul Collect Charge", "History CC (View)", "History DN (View)", "History Invoice E (View)", "History Storage (View)", "History Invoice A (View)", "History Cargo (View)", "Modul Report Clearance",
    "Invoice Export", "Invoice Import", "Invoice Domestic", "Invoice History", "Invoice C - Edit", "Report", "Faktur Pajak Report", "Gyakuso Report", "Tanda Terima Report", 
    "Overweight", "Add Customer", "Import Data", "Customers List", 
    "Import Pricelist", "Pricelist", "Special Rate", "Zone", "Master Station", "Fuel Surcharge"
];

const UserManagementPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({ id: '', name: '', email: '', password: '', role: 'USER', permissions: [] });

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/users`, { headers: { 'Authorization': `Bearer ${token}` } });
            setUsers(res.data);
        } catch (error) { toast.error("Gagal mengambil data user"); } 
        finally { setIsLoading(false); }
    };

    const openAddModal = () => {
        setFormData({ id: '', name: '', email: '', password: '', role: 'USER', permissions: [] });
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const openEditModal = (u) => {
        let perms = [];
        try { perms = JSON.parse(u.permissions || "[]"); } catch(e) { perms = []; }
        setFormData({ id: u.id, name: u.name, email: u.email, password: '', role: u.role || 'USER', permissions: perms });
        setIsEditMode(true);
        setIsModalOpen(true);
    };

    const togglePermission = (menu) => {
        if (formData.permissions.includes(menu)) {
            setFormData({ ...formData, permissions: formData.permissions.filter(m => m !== menu) });
        } else {
            setFormData({ ...formData, permissions: [...formData.permissions, menu] });
        }
    };

    const selectAllPermissions = () => {
        if (formData.permissions.length === AVAILABLE_MENUS.length) setFormData({ ...formData, permissions: [] });
        else setFormData({ ...formData, permissions: [...AVAILABLE_MENUS] });
    };

    const handleSave = async () => {
        if (!formData.name || !formData.email) return toast.error("Nama dan Email wajib diisi!");
        if (!isEditMode && !formData.password) return toast.error("Password awal wajib diisi untuk user baru!");

        const payload = { 
            name: formData.name,
            email: formData.email,
            role: formData.role,
            permissions: JSON.stringify(formData.permissions) 
        };
        if (formData.password) payload.password = formData.password;

        const toastId = toast.loading("Menyimpan data...");
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (isEditMode) await axios.put(`${API_URL}/users/${formData.id}`, payload, config);
            else await axios.post(`${API_URL}/users`, payload, config);
            
            toast.success("Data User berhasil disimpan!", { id: toastId });
            setIsModalOpen(false);
            fetchUsers();
        } catch (error) { 
            // INI KACA PEMBESARNYA: Menampilkan wujud asli error dari backend
            let errMsg = error.response?.data?.detail;
            if (Array.isArray(errMsg)) errMsg = JSON.stringify(errMsg); // Kalau errornya bentuk array
            toast.error(`Gagal: ${errMsg || error.message}`, { id: toastId, duration: 8000 }); 
        }
    };

    const handleUnblock = async (id, name) => {
        if (!window.confirm(`Yakin ingin membuka blokir akun ${name}?`)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/users/${id}/unblock`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Akun ${name} berhasil dibuka blokir!`);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Gagal membuka blokir");
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`YAKIN INGIN MENGHAPUS AKUN "${name}"?`)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/users/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success("User berhasil dihapus");
            fetchUsers();
        } catch (error) { toast.error("Gagal menghapus user"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6">
            <div className="mb-4">
                <button onClick={() => navigate('/portal')} className="flex items-center gap-2 px-4 py-2 bg-white text-slate-600 hover:text-blue-600 hover:bg-blue-50 font-bold rounded-xl shadow-sm border border-slate-200 transition">
                    <ArrowLeft size={18} /> Kembali ke Portal
                </button>
            </div>
            <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="text-red-500"/> User Management</h1>
                    <p className="text-gray-500 mt-1">Atur akses kunci (Role) dan Modul untuk setiap staf.</p>
                </div>
                <button onClick={openAddModal} className="bg-gray-900 hover:bg-black text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg transition">
                    <UserPlus size={18}/> Tambah Akun Baru
                </button>
            </div>
            <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-4 font-bold text-gray-600">Nama Lengkap</th>
                            <th className="px-6 py-4 font-bold text-gray-600">Email</th>
                            <th className="px-6 py-4 font-bold text-center text-gray-600">Role / Hak Akses</th>
                            <th className="px-6 py-4 font-bold text-center text-gray-600">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? <tr><td colSpan="4" className="p-8 text-center text-gray-400">Memuat data user...</td></tr> : 
                            users.map(u => (
                            <tr key={u.id} className={`hover:bg-gray-50 transition ${u.is_blocked == 1 ? 'bg-red-50' : ''}`}>
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-900 flex items-center gap-2">
                                        {u.name}
                                        {u.is_blocked == 1 && <span className="text-[10px] bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-black uppercase flex items-center gap-1"><ShieldAlert size={10}/> Diblokir</span>}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-blue-600 font-medium">{u.email}</td>
                                <td className="px-6 py-4 text-center">
                                    <span className={`px-3 py-1 text-[10px] font-black rounded-full uppercase tracking-widest border ${u.role === 'ADMIN' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                                        {u.role || 'USER'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <div className="flex justify-center gap-2">
                                        <button onClick={() => openEditModal(u)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"><Edit size={18}/></button>
                                        {u.is_blocked == 1 ? (
                                            <button onClick={() => handleUnblock(u.id, u.name)} className="p-2 text-orange-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition" title="Buka Blokir"><ShieldOff size={18}/></button>
                                        ) : (
                                            <button onClick={() => handleDelete(u.id, u.name)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 size={18}/></button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col w-full max-w-3xl max-h-[90vh]">
                        <div className="bg-gray-900 p-5 flex justify-between items-center text-white shrink-0">
                            <h3 className="font-bold text-xl flex items-center gap-2">{isEditMode ? 'Edit User & Akses' : 'Buat Akun Baru'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="hover:bg-gray-700 p-2 rounded-xl transition"><X size={20}/></button>
                        </div>
                        <div className="p-6 overflow-y-auto bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="font-black text-gray-800 border-b pb-2">Identitas Login</h4>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nama Lengkap</label><input type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" /></div>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email Login</label><input type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium" /></div>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Password</label><input type="text" placeholder={isEditMode ? 'Kosongkan jika tidak mau diganti' : 'Minimal 6 Karakter'} value={formData.password} onChange={e=>setFormData({...formData, password: e.target.value})} className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono" /></div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 mt-4">Pilih Level Akses</label>
                                    <select value={formData.role} onChange={e=>setFormData({...formData, role: e.target.value})} className="w-full p-2.5 border rounded-xl font-bold outline-none bg-white">
                                        <option value="USER">Staff</option>
                                        <option value="SUPER_USER">Super User</option>
                                        <option value="ADMIN">Admin</option>
                                    </select>
                                    
                                    {/* Info box dinamis sesuai role yang dipilih */}
                                    {formData.role === "ADMIN" && <p className="text-xs text-red-600 font-bold bg-red-50 p-2 rounded-lg mt-2">⚠️ ADMIN memiliki akses ke SEMUA MENU & FITUR tanpa terkecuali.</p>}
                                    {formData.role === "SUPER_USER" && <p className="text-xs text-orange-600 font-bold bg-orange-50 p-2 rounded-lg mt-2">⚠️ SUPER USER bisa Tambah/Edit/Hapus data, tapi TIDAK BISA mengelola akun staf.</p>}
                                    {formData.role === "USER" && <p className="text-xs text-blue-600 font-bold bg-blue-50 p-2 rounded-lg mt-2">ℹ️ STAFF hanya bisa Melihat dan Download data (Tombol Tambah/Edit/Hapus akan disembunyikan).</p>}
                                </div>
                            </div>
                            <div className={`space-y-3 ${formData.role === 'ADMIN' ? 'opacity-40 pointer-events-none' : ''}`}>
                                <div className="flex justify-between items-center border-b pb-2">
                                    <h4 className="font-black text-gray-800">Centang Modul & Menu</h4>
                                    <button onClick={selectAllPermissions} className="text-xs font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded">Pilih Semua</button>
                                </div>
                                <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                    {AVAILABLE_MENUS.map(menu => {
                                        const isMainModule = menu.startsWith("Modul"); 
                                        return (
                                            <div key={menu} onClick={() => togglePermission(menu)} className={`p-2 border rounded-xl flex items-center gap-3 cursor-pointer transition ${formData.permissions.includes(menu) ? (isMainModule ? 'bg-indigo-100 border-indigo-300' : 'bg-blue-50 border-blue-200') : 'bg-white hover:bg-gray-50'}`}>
                                                {formData.permissions.includes(menu) ? <CheckSquare className={isMainModule ? "text-indigo-600" : "text-blue-600"} size={20}/> : <Square className="text-gray-300" size={20}/>}
                                                <span className={`font-bold text-sm ${formData.permissions.includes(menu) ? (isMainModule ? 'text-indigo-900' : 'text-blue-900') : 'text-gray-600'}`}>{menu}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-5 border-t flex justify-end gap-3 shrink-0">
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-600 hover:bg-gray-100 px-5 py-2.5 rounded-xl font-bold transition">Batal</button>
                            <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-blue-200 transition">
                                <Save size={18}/> Simpan Akun
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagementPage;